import { Check, ChevronsUpDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { CurrencyApi } from '@/types/api/companies';

interface CurrencyComboboxProps {
  readonly currencies: CurrencyApi[];
  readonly value?: string;
  readonly onValueChange: (value: string) => void;
  readonly onBlur?: () => void;
  readonly placeholder?: string;
  readonly emptyText?: string;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
}

export function CurrencyCombobox({
  currencies,
  value,
  onValueChange,
  onBlur,
  placeholder = 'Select currency',
  emptyText = 'No currency found.',
  className,
  disabled = false,
  invalid = false,
}: CurrencyComboboxProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const close = () => setOpen(false);

    document.addEventListener('visibilitychange', close);
    window.addEventListener('blur', close);

    return () => {
      document.removeEventListener('visibilitychange', close);
      window.removeEventListener('blur', close);
    };
  }, [open]);

  const selected = currencies.find((c) => c.code === value);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          onBlur?.();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={invalid || undefined}
          disabled={disabled}
          className={cn('w-full justify-between h-9 font-normal', className)}
        >
          <span
            className={cn('truncate', !selected && 'text-muted-foreground')}
          >
            {selected ? selected.code : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search currencies..." />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {currencies.map((currency) => (
                <CommandItem
                  key={currency.id}
                  value={`${currency.code} ${currency.name}`}
                  onSelect={() => {
                    onValueChange(currency.code);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === currency.code ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-sm font-medium">
                      {currency.code}
                      {currency.symbol ? ` (${currency.symbol})` : ''}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">
                      {currency.name}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
